// Phone-number directory hashing.
//
// Both the user's OWN phone and the hashes we look up for contact
// matching go through the same `hashPhone` so they collide on equal
// numbers regardless of which device the user entered them on.
//
// Privacy contract (load-bearing):
//   * Raw phone numbers NEVER leave the client. The server only
//     receives sha-256 hex digests.
//   * Hashing folds in a global app pepper so the directory isn't
//     trivially rainbow-table-able if the DB ever leaks. Rotating
//     the pepper invalidates every existing hash (and disconnects
//     every directory match), so don't rotate casually.
//
// Pepper sourcing: REACT_APP_PHONE_HASH_PEPPER. Falls back to a
// public placeholder so dev builds work — production MUST set this
// in the deployment env (Vercel / CRA build args). Mirror the same
// value in the Supabase function env (PHONE_HASH_PEPPER) if any
// server-side hashing is ever added.

const PEPPER = process.env.REACT_APP_PHONE_HASH_PEPPER || "booda-dev-phone-pepper";

// Normalize anything the user might paste — "(415) 555-1212",
// "+1-415-555-1212", "415.555.1212" — to E.164. Returns null if we
// can't confidently produce a number. Naive North-America-first:
// 10 digits → +1XXXXXXXXXX; 11 digits starting with 1 → +1XXXXXXXXXX;
// anything starting with + → keep digits after the +. Good enough
// for v1; we can graduate to libphonenumber later if international
// users start mattering.
export function normalizeToE164(input) {
  if (input == null) return null;
  const raw = String(input).trim();
  if (!raw) return null;
  const startsPlus = raw.startsWith("+");
  const digits = raw.replace(/\D+/g, "");
  if (!digits) return null;
  if (startsPlus) return `+${digits}`;
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  // 12+ digits with no plus is probably a paste from international
  // copy; trust it and prefix +.
  if (digits.length >= 11) return `+${digits}`;
  return null;
}

// sha256(pepper || e164) → lower-case hex.
export async function hashPhone(e164) {
  if (!e164) return null;
  const enc = new TextEncoder();
  const data = enc.encode(`${PEPPER}${e164}`);
  const digest = await crypto.subtle.digest("SHA-256", data);
  const bytes = new Uint8Array(digest);
  let hex = "";
  for (let i = 0; i < bytes.length; i++) {
    hex += bytes[i].toString(16).padStart(2, "0");
  }
  return hex;
}

// Convenience: normalize + hash in one call. Returns null on input
// we couldn't parse.
export async function hashRawPhone(input) {
  const e164 = normalizeToE164(input);
  if (!e164) return null;
  return hashPhone(e164);
}

// Batch helper for contact-list ingestion. Skips unparseable inputs
// silently — the caller passes the entire device address book; not
// every entry will be a phone.
export async function hashContactBatch(rawPhones) {
  if (!Array.isArray(rawPhones)) return [];
  const out = [];
  for (const raw of rawPhones) {
    const e164 = normalizeToE164(raw);
    if (!e164) continue;
    const h = await hashPhone(e164);
    if (h) out.push({ raw, e164, hash: h });
  }
  return out;
}
